import React from "react";
import { Link } from "react-router-dom";
import { CATEGORY_CIRCLES } from "@/data/products";
import { MiniGiftBox } from "@/components/MiniGiftBox";

/**
 * Round category shortcuts row — Mini Box / Glam Box up front (as the
 * finalised design), followed by the standard category circles.
 */

const BOX_CIRCLES = [
  {
    id: "it-girl",
    label: "It-Girl Box",
    pill: "₹499 · 3 pcs",
    image: "/circles/it-girl.webp",
    pos: "50% 60%",
    to: "/bundle/it-girl",
    ringGradient:
      "conic-gradient(from 140deg, #E8A0B4, #D4537E, #B33866, #E8A0B4)",
    pillBg: "linear-gradient(135deg, #D4537E, #B33866)",
  },
  {
    id: "glam",
    label: "Glam Box",
    pill: "₹799 · 5 pcs",
    image: "/circles/glam.webp",
    pos: "40% 45%",
    to: "/bundle/glam",
    ringGradient:
      "conic-gradient(from 140deg, #E0BC76, #F4D58D, #C5985A, #E0BC76)",
    pillBg: "linear-gradient(135deg, #4A2C35, #2B1A20)",
  },
  {
    id: "dream",
    label: "Dream Box",
    pill: "₹1199 + Gift",
    gift: true,
    image: "/circles/dream.webp",
    pos: "68% 28%",
    to: "/bundle/dream",
    ringGradient:
      "conic-gradient(from 140deg, #4A2C35, #D4537E, #C5985A, #4A2C35)",
    pillBg: "linear-gradient(135deg, #4A2C35, #D4537E)",
  },
];

const BoxCircle = ({ box }) => (
  <Link
    to={box.to}
    className="group flex-shrink-0 text-center"
    data-testid={`circle-box-${box.id}`}
  >
    <div
      className="relative w-[72px] h-[72px] sm:w-[88px] sm:h-[88px] lg:w-[104px] lg:h-[104px] rounded-full p-[3px] mb-2.5 transition-transform group-hover:-translate-y-0.5"
      style={{ background: box.ringGradient }}
    >
      <div className="absolute inset-1 rounded-full overflow-hidden bg-pearl">
        <img
          src={box.image}
          alt={box.label}
          style={{ objectPosition: box.pos }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
      </div>
      {box.gift && <MiniGiftBox size={24} className="absolute -top-1 -right-1 ring-2 ring-cream" />}
    </div>
    <p className="text-[12px] sm:text-[13px] font-medium tracking-wide text-plum group-hover:text-blush transition-colors">
      {box.label}
    </p>
    <span
      className="inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] tracking-[0.16em] uppercase font-semibold text-cream"
      style={{ background: box.pillBg }}
      data-testid={`circle-box-${box.id}-pill`}
    >
      {box.pill}
    </span>
  </Link>
);

const CategoryCircles = () => (
  <section
    className="py-8 lg:py-12"
    style={{ background: "#FDF6F0" }}
    data-testid="category-circles"
  >
    <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-10">
      <div className="flex items-center justify-between mb-5 lg:mb-7">
        <p className="overline">Shop the Edit</p>
        <Link
          to="/shop"
          className="text-[11px] tracking-[0.18em] uppercase text-gold hover:text-blush transition-colors"
          data-testid="circles-view-all"
        >
          View all →
        </Link>
      </div>
      <div className="flex gap-5 sm:gap-7 lg:gap-9 overflow-x-auto no-scrollbar snap-x-mobile -mx-4 sm:-mx-5 lg:mx-0 px-4 sm:px-5 lg:px-0 pb-2 items-start">
        {/* Mini Box + Glam Box up front */}
        {BOX_CIRCLES.map((b) => (
          <BoxCircle key={b.id} box={b} />
        ))}

        {/* Standard categories */}
        {CATEGORY_CIRCLES.map((c) => (
          <Link
            key={c.id}
            to={`/shop?category=${c.id}`}
            className="group flex-shrink-0 text-center"
            data-testid={`circle-${c.id}`}
          >
            <div className="relative w-[72px] h-[72px] sm:w-[88px] sm:h-[88px] lg:w-[104px] lg:h-[104px] rounded-full overflow-hidden mb-2.5 ring-2 ring-sectionbreak transition-all group-hover:ring-gold group-hover:ring-offset-2 group-hover:ring-offset-cream">
              <div className="absolute inset-1 rounded-full overflow-hidden bg-pearl">
                <img
                  src={c.image}
                  alt={c.label}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              </div>
            </div>
            <p className="text-[12px] sm:text-[13px] font-medium tracking-wide text-plum group-hover:text-blush transition-colors">
              {c.label}
            </p>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default CategoryCircles;
